// components/ui/SunburstCrest.tsx — 16-spike gold sunburst medallion

import type { SVGProps } from 'react';

interface SunburstCrestProps extends SVGProps<SVGSVGElement> {
  size?: number;
}

/**
 * 16-spike sunburst medallion built from SVG triangles.
 * Fill: radial gradient from --gold-hi (centre) to --gold-deep (edge).
 * Stroke: dark gold #5c4420 at 0.6px.
 */
export function SunburstCrest({ size = 64, className, ...rest }: SunburstCrestProps) {
  const cx = 50;
  const cy = 50;
  const outerR = 42;   // spike tip
  const innerR = 24;   // spike base / inner ring
  const coreR  = 16;   // solid centre circle
  const spikes = 16;

  const spikePaths: string[] = [];
  for (let i = 0; i < spikes; i++) {
    const midAngle  = (i / spikes) * Math.PI * 2 - Math.PI / 2;
    const leftAngle = midAngle - (Math.PI / spikes) * 0.45;
    const rightAngle= midAngle + (Math.PI / spikes) * 0.45;

    const tip   = { x: cx + outerR * Math.cos(midAngle),   y: cy + outerR * Math.sin(midAngle) };
    const left  = { x: cx + innerR * Math.cos(leftAngle),  y: cy + innerR * Math.sin(leftAngle) };
    const right = { x: cx + innerR * Math.cos(rightAngle), y: cy + innerR * Math.sin(rightAngle) };

    spikePaths.push(
      `M ${left.x.toFixed(2)},${left.y.toFixed(2)} L ${tip.x.toFixed(2)},${tip.y.toFixed(2)} L ${right.x.toFixed(2)},${right.y.toFixed(2)} Z`
    );
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={className}
      {...rest}
    >
      <defs>
        {/* Gold foil radial gradient */}
        <radialGradient id="crestFill" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
          <stop offset="0%"   stopColor="#F0D9A0" />   {/* gold-hi centre */}
          <stop offset="45%"  stopColor="#D9B978" />   {/* gold-light mid */}
          <stop offset="100%" stopColor="#7d5f2e" />   {/* gold-deep edge */}
        </radialGradient>
        {/* Subtle inner shadow for depth */}
        <radialGradient id="crestSheen" cx="35%" cy="30%" r="60%">
          <stop offset="0%"   stopColor="rgba(255,255,255,0.30)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0.00)" />
        </radialGradient>
      </defs>

      {/* Spike triangles */}
      {spikePaths.map((d, i) => (
        <path
          key={i}
          d={d}
          fill="url(#crestFill)"
          stroke="#5c4420"
          strokeWidth="0.6"
          strokeLinejoin="round"
        />
      ))}

      {/* Inner ring connecting spike bases */}
      <circle
        cx={cx} cy={cy} r={innerR + 1}
        fill="url(#crestFill)"
        stroke="#5c4420"
        strokeWidth="0.6"
      />

      {/* Centre medallion */}
      <circle
        cx={cx} cy={cy} r={coreR}
        fill="url(#crestFill)"
        stroke="#5c4420"
        strokeWidth="0.8"
      />

      {/* Sheen highlight */}
      <circle cx={cx} cy={cy} r={innerR + 1} fill="url(#crestSheen)" />
    </svg>
  );
}
